import React, { useEffect, useMemo, useState } from 'react';
import {
	NavigationStepperComponentParams,
	NavigationStepperItemType,
} from '../../../../Components/Stepper/NavigationStepper';
import { usePatientTreatmentContext } from './ContextProvider';
import { useAppContext } from '../../../../Services/AppContext';
import EducationMaterials from '../../../../Components/EducationMaterials/EducationMaterials';
import RequestPaymentModal from '../../../../Components/RequestPayment/RequestPaymentModal';
import SubTabNavigationStepper from '../../../../Components/Stepper/SubTabNavigationStepper/SubTabNavigationStepper';
import PatientTreatmentPageWrapper from './PatientTreatmentPageWrapper';
import { useAclContext } from '../../../Acl/Components/AclContextProvider';
import BaseHealthData from '../../../HealthData/Components/BaseHealthData';
import useSms from '../../Hooks/useSms';
import CaseForwarderModal from '../../../Case/Components/CaseForwarder/CaseForwarderModal';
import PatientsQuestionnaires from '../../../Questionnaire/Components/PatientsQuestionnaires/PatientsQuestionnaires';
import ConfirmationModal from '../../../Base/Components/Confirmation/ConfirmationModal';
import Button from '../../../../Components/Button/Button';
import { Theme } from '../../../../Enums/Theme.enum';
import InsuranceReportsList from '../../../Insurance/Component/InsuranceReportsList/InsuranceReportsList';
import TransmiseList from '../../../TransmiseModule/Component/TransmiseList/TransmiseList';
import CaseTakeOverModal from '../../../Case/Components/CaseTakeOver/CaseTakeOverModal';
import PatientTreatmentLaboratories from './PatientTreatmentLaboratories';
import PatientMedicalHistory from '../PatientMedicalHistory/PatientMedicalHistory';
import MedicalReportsListWrapper from '../../../MedicalReport/Components/MedicalReportsListWrapper';
import apiClient from '../../../../Services/ApiClient';
import useApiClient from '../../../Base/Hooks/useApiClient';
import useCustomAclForPatient from '../../../Acl/Hooks/useCustomAclForPatient';
import CSSZSickLeaveCertificateWrapper from '../../../CSSZSickLeaveCertificate/Components/CSSZSickLeaveCertificateWrapper/CSSZSickLeaveCertificateWrapper';
import DiscourseWrapper from '../../../Discourse/Components/Discourse/DiscourseWrapper';
import MedicalRequestsListWrapper from '../../../MedicalRequests/Components/MedicalRequestsList/MedicalRequestsListWrapper';
import { MzcrDrivingLicenseMedicalReport } from '@meddihub/mzcr-ui';
import MzcrPatientService from '../../../Mzcr/Components/MzcrPatientService';
import { getMzcrClient } from '../../../Mzcr/Services/mzcrClient';
import useMzcrAccess from '../../../Mzcr/Services/useMzcrAccess';
import PatientTreatmentSharedHealthRecord from './SharedHealthRecord';

interface Props {
	workplaceId: string;
}

const PatientTreatmentTreatment: React.FC<Props> = ({ workplaceId }) => {
	const { patientId, caseId, isMyCase } = usePatientTreatmentContext();
	const { isMou } = useAppContext();
	const [steps, setSteps] = useState<NavigationStepperItemType[]>([]);
	const [showRequestPayment, setShowRequestPayment] = useState(false);
	const [showCaseForwarder, setShowCaseForwarder] = useState(false);
	const [isCaseTakeOverOpen, setIsCaseTakeOverOpen] = useState(false);
	const [isSmsConfirmationOpen, setIsSmsConfirmationOpen] = useState(false);
	const [smsConfirmationText, setSmsConfirmationText] = useState('');
	const { isLaboratoryRequestsAllowed } = useCustomAclForPatient(patientId);
	const { isAllowed } = useAclContext();
	const mzcrAccess = useMzcrAccess();
	const { sendSms, getText } = useSms();
	const { displayError } = useApiClient();

	useEffect(() => {
		handleLoadSteps();
	}, [isLaboratoryRequestsAllowed, caseId, isMou, mzcrAccess.isVisible]);

	const handleSendSmsNotification = async (caseId: string) => {
		const result = await sendSms(caseId);
		if (result) {
			const smsText = await getText(caseId);
			if (smsText) {
				setSmsConfirmationText(smsText);
				setIsSmsConfirmationOpen(true);
			}
		}
	};

	const handleRequestBankIdVerification = async () => {
		try {
			await apiClient.post(`/case/${caseId}/bank-id`, {});
		} catch (e) {
			displayError(e);
		}
	};

	const handleLoadSteps = () => {
		const tempSteps: NavigationStepperItemType[] = [
			{
				name: 'patientTreatment.cure.dekurz.title',
				key: 'discourse',
				component: (params: NavigationStepperComponentParams) => (
					<PatientTreatmentPageWrapper patientId={patientId}>
						<DiscourseWrapper
							caseId={caseId}
							patientId={patientId}
							workplaceId={workplaceId}
						/>
					</PatientTreatmentPageWrapper>
				),
			},
		];

		if (isAllowed('patient_health_data')) {
			tempSteps.push({
				name: 'patientTreatment.cure.healthData.title',
				key: 'healthData',
				disabled: isMou,
				component: (params: NavigationStepperComponentParams) => (
					<PatientTreatmentPageWrapper patientId={patientId}>
						<BaseHealthData patientId={patientId} />
					</PatientTreatmentPageWrapper>
				),
			});
		}

		if (isAllowed('medical_report')) {
			tempSteps.push({
				name: 'patientTreatment.cure.medicalReports.title',
				key: 'medicalReports',
				component: (params: NavigationStepperComponentParams) => (
					<PatientTreatmentPageWrapper patientId={patientId}>
						<MedicalReportsListWrapper
							patientId={patientId}
							workplaceId={workplaceId}
							caseId={caseId}
						/>
					</PatientTreatmentPageWrapper>
				),
			});
		}

		if (mzcrAccess.isVisible) {
			tempSteps.push({
				name: 'patientTreatment.cure.drivingLicenseMedicalReports.title',
				key: 'drivingLicenseMedicalReports',
				component: () => (
					<PatientTreatmentPageWrapper patientId={patientId}>
						<MzcrPatientService patientId={patientId}>
							{service => (
								<MzcrDrivingLicenseMedicalReport
									key={service.rid}
									mzcrClient={getMzcrClient()}
									{...service}
								/>
							)}
						</MzcrPatientService>
					</PatientTreatmentPageWrapper>
				),
			});
		}

		if (isAllowed('medical_request')) {
			tempSteps.push({
				name: 'patientTreatment.cure.requestForms.title',
				key: 'medicalRequests',
				component: (params: NavigationStepperComponentParams) => (
					<PatientTreatmentPageWrapper patientId={patientId}>
						<MedicalRequestsListWrapper
							patientId={patientId}
							workplaceId={workplaceId}
							setIsLoading={params.setIsLoading}
						/>
					</PatientTreatmentPageWrapper>
				),
			});
		}

		if (isLaboratoryRequestsAllowed) {
			tempSteps.push({
				name: 'patientTreatment.cure.laboratoryRequests.title',
				key: 'laboratoryRequests',
				component: () => (
					<PatientTreatmentPageWrapper patientId={patientId}>
						<PatientTreatmentLaboratories
							patientId={patientId}
							workplaceId={workplaceId}
						/>
					</PatientTreatmentPageWrapper>
				),
			});
		}

		if (isAllowed('manager_sick_leaves')) {
			tempSteps.push({
				name: 'patientTreatment.cure.CSSZSickLeaveCertificate.title',
				key: 'CSSZSickLeaveCertificate',
				component: () => (
					<PatientTreatmentPageWrapper patientId={patientId}>
						<CSSZSickLeaveCertificateWrapper
							patientId={patientId}
							workplaceId={workplaceId}
						/>
					</PatientTreatmentPageWrapper>
				),
			});
		}

		if (isAllowed('questionnaire')) {
			tempSteps.push({
				name: 'patientTreatment.cure.questionnaires.title',
				key: 'questionnaires',
				component: (params: NavigationStepperComponentParams) => (
					<PatientTreatmentPageWrapper patientId={patientId}>
						<PatientsQuestionnaires patientId={patientId} />
					</PatientTreatmentPageWrapper>
				),
			});
		}

		if (isAllowed('education_material')) {
			tempSteps.push({
				name: 'patientTreatment.cure.educationMaterials.title',
				key: 'educationMaterials',
				component: (params: NavigationStepperComponentParams) => (
					<PatientTreatmentPageWrapper patientId={patientId}>
						<EducationMaterials
							patientId={patientId}
							workplaceId={workplaceId}
						/>
					</PatientTreatmentPageWrapper>
				),
			});
		}

		if (isAllowed('medicalHistory')) {
			tempSteps.push({
				name: 'patientTreatment.cure.medicalHistory.title',
				key: 'medicalHistory',
				component: (params: NavigationStepperComponentParams) => (
					<PatientTreatmentPageWrapper patientId={patientId}>
						<PatientMedicalHistory patientId={patientId} />
					</PatientTreatmentPageWrapper>
				),
			});
		}

		if (mzcrAccess.isVisible) {
			tempSteps.push({
				name: 'patientTreatment.cure.sharedHealthRecord.title',
				key: 'sharedHealthRecord',
				component: (params: NavigationStepperComponentParams) => (
					<PatientTreatmentPageWrapper patientId={patientId}>
						<PatientTreatmentSharedHealthRecord
							patientId={patientId}
							onOpenAnamnesis={() => params.setActiveStepPath('medicalHistory')}
						/>
					</PatientTreatmentPageWrapper>
				),
			});
		}

		if (isAllowed('insuranceReport')) {
			tempSteps.push({
				name: 'base.insuranceReports',
				key: 'insuranceReports',
				component: (params: NavigationStepperComponentParams) => (
					<PatientTreatmentPageWrapper patientId={patientId}>
						<InsuranceReportsList patientId={patientId} />
					</PatientTreatmentPageWrapper>
				),
			});
		}

		if (isAllowed('transmise')) {
			tempSteps.push({
				name: 'base.transmission',
				key: 'transmise',
				component: (params: NavigationStepperComponentParams) => (
					<PatientTreatmentPageWrapper patientId={patientId}>
						<TransmiseList patientId={patientId} />
					</PatientTreatmentPageWrapper>
				),
			});
		}

		if (caseId) {
			if (isAllowed('notificationUsingSMS')) {
				tempSteps.push({
					name: 'component.sms.sendSMS',
					key: 'notificationUsingSMS',
					onClick: () => handleSendSmsNotification(caseId),
				});
			}

			if (isAllowed('payment_request', 'create')) {
				tempSteps.push({
					name: 'patientTreatment.cure.requestPayment.title',
					key: 'requestPayment',
					onClick: () => setShowRequestPayment(true),
				});
			}

			if (isAllowed('bankid_verification_request', 'create')) {
				tempSteps.push({
					name: 'patientTreatment.cure.requestBankIdVerification.title',
					key: 'requestBankIdVerification',
					onClick: () => handleRequestBankIdVerification(),
				});
			}

			tempSteps.push({
				name: 'component.caseForwarder.passTheCaseToOtherMedicOrWorkplace',
				key: 'showCaseForwarder',
				onClick: () => setShowCaseForwarder(true),
				// component: () => <CaseForwarder isToggled caseId={caseId} />,
			});
			if (!isMyCase) {
				tempSteps.push({
					name: 'base.takeOverTheCase',
					key: 'takeOverTheCase',
					onClick: () => setIsCaseTakeOverOpen(true),
				});
			}
		}

		setSteps(tempSteps);
	};

	const initialStepPath = useMemo(() => {
		const requestedStep = new URLSearchParams(window.location.search).get(
			'treatmentStep',
		);

		if (requestedStep && steps.some(step => step.key === requestedStep)) {
			return requestedStep;
		}

		return steps[0]?.key;
	}, [steps]);

	if (!steps.length) {
		return <></>;
	}

	return (
		<>
			<SubTabNavigationStepper
				steps={steps}
				initialStepPath={initialStepPath}
				canSkip={true}
			/>
			{!!caseId && showRequestPayment && (
				<RequestPaymentModal
					isOpen
					caseId={caseId}
					onClose={() => setShowRequestPayment(false)}
				/>
			)}
			{!!caseId && showCaseForwarder && (
				<CaseForwarderModal
					opened={showCaseForwarder}
					caseId={caseId}
					onClose={() => setShowCaseForwarder(false)}
				/>
			)}
			{!!caseId && isCaseTakeOverOpen && (
				<CaseTakeOverModal
					isOpen
					caseId={caseId}
					onClose={() => setIsCaseTakeOverOpen(false)}
				/>
			)}
			{!!caseId && isSmsConfirmationOpen && (
				<ConfirmationModal
					title='component.sms.smsSentSuccessfully'
					text={smsConfirmationText}
					isOpen={isSmsConfirmationOpen}
					onClose={() => setIsSmsConfirmationOpen(false)}>
					<Button
						theme={Theme.GREEN}
						title='base.shared.ok'
						onClick={() => setIsSmsConfirmationOpen(false)}
					/>
				</ConfirmationModal>
			)}
		</>
	);
};

export default PatientTreatmentTreatment;
